@ -1,221 +0,0 @@
<script setup lang="ts">
import { flattenEntity } from 'strapi-flatten-graphql'
import { Menu, MenuButton, MenuItem, MenuItems, Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import type { SalaryReportData } from '~/models/SalaryReport'
import type { HeaderSalary } from '~/models/Strapi'

interface Props {
  datas: HeaderSalary
}

const props = defineProps<Props>()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const modalOpen = ref(false)
const isLoading = ref(false)
const flattenData = { ...props.datas, item: props.datas.item.data.map((subitem: any) => (flattenEntity(subitem))) }
const selectedTab = ref(!isEmpty(route.query?.term) ? `${flattenData.tabs.find(obj => obj.value === route.query.term)?.key}|1` : `${flattenData.default_key}|1`)
const selectedTabItem = computed(() => flattenData.item.find(obj => obj.key === selectedTab.value.split('|')[0]))
const salaryResult = ref<SalaryReportData>()
const selectedTitle = computed(() => route.query?.term ? route.query?.term : flattenData?.default_key)
const selectedLoc = computed(() => route.query?.loc ? route.query?.loc : 'anywhere')
const selectedLevel = computed(() => route.query?.level ? route.query?.level : 'freshgraduate')
const level = [
  { value: 'freshgraduate', label: t('salary_page_freshgraduate_title'), description: t('salary_page_freshgraduate_desc') },
  { value: 'junior', label: t('salary_page_entry_title'), description: t('salary_page_entry_desc') },
  { value: 'senior', label: t('salary_page_senior_title'), description: t('salary_page_senior_desc') },
]
const location = [
  { value: 'anywhere', label: t('salary_page_everywhere'), currency: 'USD' },
  { value: 'indonesia', label: t('salary_page_indonesia'), currency: 'IDR' },
  { value: 'philippines', label: t('salary_page_philippines'), currency: 'PHP' },
  { value: 'malaysia', label: t('salary_page_malaysia'), currency: 'MYR' },
]

function changeFilter(newTerm: string | string[], newLoc: string | string[], newLevel: string | string[]) {
  router.push({
    path: route.path,
    query: {
      term: newTerm,
      loc: newLoc,
      level: newLevel,
    },
  })
  selectedTab.value = `${flattenData.tabs.find(obj => obj.value === newTerm)?.key}|1`
  getSalary(newTerm, newLoc, newLevel, location.find(data => data.value === newLoc)?.currency || 'USD')
}

async function getSalary(title: string | string[], loc: string | string[], lvl: string | string[], crnc: string) {
  isLoading.value = true
  const { data, error } = await useCompanyApi('/talent/job/salary/report', {
    method: 'POST',
    body: {
      jobTitle: title,
      location: loc,
      level: lvl,
      currency: crnc,
      period: 'monthly',
    },
    initialCache: false,
  })

  const resp = data.value as { success: boolean; data: SalaryReportData }
  if (resp && resp.success)
    salaryResult.value = resp.data

  isLoading.value = false
}
getSalary(selectedTitle.value, selectedLoc.value, selectedLevel.value, location.find(data => data.value === selectedLoc.value)?.currency || 'USD')
</script>

<template>
  <BaseAtomsSkeletonBounded y-padding="lg" class="text-gray-900 !p-0 grid">
    <!-- header title -->
    <div class="w-full mx-auto text-center mb-10 px-4 lg:px-0">
      <ContinueFormModal :open="modalOpen" :redirect-val="flattenData?.button_other_url" @open-modal="(item: boolean) => modalOpen = item" />
      <p class="font-bold text-4xl mb-4 font-serif">
        {{ flattenData?.title }}
      </p>
      <p class="text-sm text-gray-600">
        {{ flattenData?.subtitle }}
      </p>
    </div>

    <!-- job title list -->
    <div class="mx-auto md:flex gap-x-4 text-center">
      <div class="flex md:grid grid-cols-3 gap-x-4 mb-6 md:mb-0">
        <div v-for="(item, idx) in flattenData?.tabs" :key="idx" :class="`${flattenData.tabs.find(obj => (obj.key === selectedTitle || obj.value === selectedTitle))?.value === item.value ? 'bg-[#5843E5] text-white' : 'bg-gray-100 text-gray-600'} text-center font-bold rounded-md px-4 py-2.5 cursor-pointer`" @click="changeFilter(item.value, selectedLoc, selectedLevel)">
          {{ item.label }}
        </div>
      </div>
      <div style="width: fit-content" class="mx-auto text-center border-indigo-500 text-indigo-500 border font-semibold rounded-md px-4 py-2.5 cursor-pointer flex items-center" @click="modalOpen = true">
        {{ flattenData?.button_other_label }}
        <Icon name="mdi:arrow-right" class="w-5 h-5 ml-2" />
      </div>
    </div>
    <div v-if="isLoading" class="lg:rounded-2xl pt-5 lg:pt-10 mt-12 lg:mt-20 w-full max-w-[956px] mx-auto px-4 lg:px-0 bg-gradient-to-r from-[#FFFFFF] to-[#F5F3FF40]">
      <div class="w-full max-w-[760px] mx-auto animate-pulse">
        <!-- location filter -->
        <div class="w-[60%] lg:w-[20%] rounded-md bg-gray-200 h-6 flex lg:block lg:ml-6 mb-4" />

        <!-- salary box -->
        <div class="bg-white shadow-xl p-6">
          <div class="w-[50%] lg:w-[20%] rounded-md bg-gray-200 h-6 mb-6 lg:mb-12" />
          <div class="flex items-center mb-4 lg:mb-20">
            <!-- salary total -->
            <div class="w-[90%] lg:w-[40%] rounded-md bg-gray-200 h-12" />

            <!-- freshgraduate filter desktop -->
            <div class="w-[30%] rounded-md bg-gray-200 h-8 hidden lg:block ml-auto" />
          </div>
          <div class="w-[50%] lg:w-[25%] rounded-md bg-gray-200 h-5" />

          <!-- freshgraduate filter mobile -->
          <div class="w-full rounded-md bg-gray-200 h-8 block lg:hidden mt-10" />
        </div>
      </div>
    </div>
    <div v-else-if="!isEmpty(salaryResult) && !isLoading" class="lg:rounded-2xl pt-5 lg:pt-10 mt-12 lg:mt-20 w-full max-w-[956px] mx-auto px-4 lg:px-0 bg-gradient-to-r from-[#FFFFFF] to-[#F5F3FF40]">
      <div class="w-full max-w-[760px] mx-auto">
        <!-- location filter -->
        <Menu v-slot="{ open }" as="div" class="flex lg:block lg:ml-6 mb-4">
          <MenuButton>
            <div class="flex items-center text-sm">
              <Icon name="mdi:earth" class="w-4 h-4 mr-2" />
              <span>{{ location.find(item => item.value === selectedLoc)?.label }}</span>
              <Icon name="heroicons:chevron-down" :class="open ? 'rotate-180 transform' : ''" class="h-4 w-4 ml-2" />
            </div>
          </MenuButton>
          <transition
            enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0"
          >
            <MenuItems class="absolute shadow-xl w-56 origin-top-right rounded-md border-0 ring-0 focus:outline-none z-10 bg-white p-3 space-y-4 cursor-pointer">
              <MenuItem v-for="(item, idx) in location" :key="idx" @click="changeFilter(selectedTitle, item.value, selectedLevel)">
                <div>{{ item.label }}</div>
              </MenuItem>
              <MenuItem @click="modalOpen = true">
                <div class="text-gray-400">
                  {{ $t('salary_page_other_country') }}
                </div>
              </MenuItem>
            </MenuItems>
          </transition>
        </Menu>

        <!-- salary box -->
        <div class="bg-white shadow-xl p-6">
          <div class="flex items-center text-gray-600 mb-6 lg:mb-12 text-sm">
            <Icon name="material-symbols:join-right" class="w-4 h-4 mr-2" />
            {{ $t('salary_page_average_salary') }}
          </div>
          <div class="flex items-center mb-4 lg:mb-20">
            <!-- salary total -->
            <div class="flex items-center">
              <p class="text-5xl font-bold break-all">
                {{ useCurrencyFormat(location.find(data => data.value === selectedLoc)?.currency || 'USD', salaryResult.averageSalary, null, false, false, false) }}
              </p>
              <span class="ml-1 text-lg"> / {{ $t('global_month') }}</span>
            </div>

            <!-- freshgraduate filter desktop -->
            <Menu v-slot="{ open }" as="div" class="hidden lg:block ml-auto">
              <MenuButton class="bg-gray-50 py-2.5 pl-4 pr-2 rounded-sm">
                <div class="flex items-center text-sm font-bold">
                  <span>{{ level.find(item => item.value === selectedLevel)?.label }}</span>
                  <Icon name="heroicons:chevron-down" :class="open ? 'rotate-180 transform' : ''" class="h-4 w-4 ml-2" />
                </div>
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0"
              >
                <MenuItems class="absolute shadow-xl w-64 origin-top-right rounded-md border-0 ring-0 focus:outline-none z-10 bg-white p-3 space-y-4 cursor-pointer">
                  <MenuItem v-for="(item, idx) in level" :key="idx" @click="changeFilter(selectedTitle, selectedLoc, item.value)">
                    <div>
                      <p class="font-bold mb-1">
                        {{ item.label }}
                      </p>
                      <p class="text-xs text-gray-400 leading-4">
                        {{ item.description }}
                      </p>
                    </div>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
          <span class="text-xs text-gray-600">{{ $t('salary_page_source') }}</span>

          <!-- freshgraduate filter mobile -->
          <Menu v-slot="{ open }" as="div" class="block lg:hidden mt-10">
            <MenuButton class="bg-gray-50 py-2.5 pl-4 pr-2 rounded-sm w-full flex">
              <div class="flex items-center text-sm font-bold mx-auto">
                <span>{{ level.find(item => item.value === selectedLevel)?.label }}</span>
                <Icon name="heroicons:chevron-down" :class="open ? 'rotate-180 transform' : ''" class="h-4 w-4 ml-2" />
              </div>
            </MenuButton>
            <transition
              enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in"
              leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0"
            >
              <MenuItems class="absolute shadow-xl w-64 origin-top-right rounded-md border-0 ring-0 focus:outline-none z-10 bg-white p-3 space-y-4 cursor-pointer">
                <MenuItem v-for="(item, idx) in level" :key="idx" @click="changeFilter(selectedTitle, selectedLoc, item.value)">
                  <div>
                    <p class="font-bold mb-1">
                      {{ item.label }}
                    </p>
                    <p class="text-xs text-gray-400 leading-4">
                      {{ item.description }}
                    </p>
                  </div>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <div class="max-w-3xl mx-auto">
      <div class="text-2xl md:text-3xl font-bold mb-4 mt-20">
        {{ selectedTabItem.title }}
      </div>
      <div class="leading-6 mb-14 last:mb-0">
        <div v-for="(item, idx) in selectedTabItem.description" :key="idx" v-html="useArticleHTMLSerializer(item)" />
      </div>

      <TabGroup>
        <TabList class="flex space-x-2">
          <Tab v-for="idx in 4" :key="idx" as="template" @click="selectedTab = `${selectedTabItem.key}|${idx}`">
            <button class="px-2 pb-2 pt-2.5" :class="`${selectedTab.split('|')[1] === idx.toString() ? 'font-bold border-b-2 border-[#5843E5] focus:ring-0 focus:outline-none text-[#212121]' : 'text-[#666666]'}`">
              {{ selectedTabItem[`tab${idx}_label`] }}
            </button>
          </Tab>
        </TabList>

        <TabPanels class="mt-6 lg:mt-10 text-sm">
          <TabPanel v-for="(item, idx) in selectedTabItem" :key="idx" class="focus:ring-0 focus:ring-offset-0 rounded-xl bg-white ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2">
            <div v-for="(item2, idx2) in selectedTabItem[`tab${selectedTab.split('|')[1]}_content`]" :key="idx2" v-html="useArticleHTMLSerializer(item2)" />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  </BaseAtomsSkeletonBounded>
</template>
